import { useEffect, useRef, useState } from 'react'
import { Avatar, Dropdown, Menu, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { darken, getContrast, rgba } from 'polished'
import { Icon } from '@components/molecules'
import { useStore } from '@core/hooks'

let nextMenuId = 0
const enter = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`
const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  &:focus-visible {
    outline: 2px solid var(--account-ink);
    outline-offset: 3px;
  }
`
const Card = styled.div`
  width: 260px;
  max-width: calc(100vw - 24px);
  padding: 8px;
  border: 1px solid #ece8f1;
  border-radius: 16px;
  background: #fff;
  color: #302a3c;
  box-shadow: 0 10px 32px #20133624;
  animation: ${enter} 160ms ease-out;
  .account-identity {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 8px 18px;
    border-bottom: 1px solid #ece8f1;
  }
  .account-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
  }
  .ant-avatar {
    flex-shrink: 0;
  }
  .ant-menu {
    border: 0;
    box-shadow: none;
    background: transparent;
    padding: 6px 0 0;
  }
  .ant-menu-item {
    display: flex;
    align-items: center;
    min-height: 44px;
    height: auto;
    line-height: 1.4;
    margin: 0 !important;
    padding: 12px !important;
    border-radius: 9px;
    color: #302a3c;
  }
  .ant-menu-item .ess-icon {
    display: inline-flex;
    color: inherit;
    flex-shrink: 0;
  }
  .ant-menu-item .ant-menu-title-content {
    margin-left: 12px;
  }
  .ant-menu-item:hover,
  .ant-menu-item-active,
  .ant-menu-item:focus-visible {
    background: var(--account-tint);
    color: var(--account-ink);
  }
  .ant-menu-item:focus-visible {
    outline: 2px solid var(--account-ink);
    outline-offset: -2px;
  }
  .ant-menu-item::after {
    display: none;
  }
  .ant-menu-item-divider {
    margin: 6px 0;
    border-color: #ece8f1;
  }
  .ant-menu-item.account-signout {
    color: #b42332;
  }
  .ant-menu-item.account-signout:hover,
  .account-signout.ant-menu-item-active,
  .account-signout:focus-visible {
    background: #fff0f1;
    color: #a51d2b;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

export default function AdminAccountMenu({
  name,
  profileImage,
  onMenuClicked
}) {
  const { t } = useTranslation()
  const [{ theme }] = useStore()
  const [open, setOpen] = useState(false)
  const [menuId] = useState(() => `admin-account-menu-${++nextMenuId}`)
  const triggerRef = useRef(null)
  const [failedImage, setFailedImage] = useState(null)
  const main = theme?.mainColorCode || '#4c2f8c'
  let ink = main
  for (let step = 1; step <= 100 && getContrast(ink, '#ffffff') < 4.5; step++) {
    ink = darken(step / 100, main)
  }
  const colors = { '--account-ink': ink, '--account-tint': rgba(ink, 0.08) }
  const image =
    profileImage && failedImage !== profileImage ? profileImage : '/avatar.png'
  const avatar = (
    <Avatar
      src={image}
      size={45}
      style={{ border: '3px solid #e6e6e6' }}
      onError={() => {
        setFailedImage(profileImage)
        return false
      }}
    />
  )
  const close = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }
  useEffect(() => {
    if (!open) return undefined
    const escape = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('keydown', escape, true)
    return () => document.removeEventListener('keydown', escape, true)
  }, [open])

  return (
    <Dropdown
      trigger={['click']}
      placement="bottomRight"
      align={{ offset: [0, 8] }}
      autoAdjustOverflow
      autoFocus
      open={open}
      onOpenChange={setOpen}
      transitionName=""
      overlay={
        <Card style={{ ...colors, ...(!open && { animation: 'none' }) }}>
          <div className="account-identity">
            {avatar}
            <Tooltip title={name || t('common.account-menu')}>
              <span className="account-name" title={name}>
                {name || t('common.account-menu')}
              </span>
            </Tooltip>
          </div>
          <Menu
            id={menuId}
            selectable={false}
            aria-label={t('common.account-menu')}
            onClick={({ key }) => {
              close()
              onMenuClicked(key)
            }}
          >
            <Menu.Item
              key="setting"
              icon={<Icon icon="Gear" size={20} color="currentColor" />}
            >
              {t('common.setting')}
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              key="signOut"
              className="account-signout"
              icon={<Icon icon="Login" size={20} color="currentColor" />}
            >
              {t('common.sign-out')}
            </Menu.Item>
          </Menu>
        </Card>
      }
    >
      <Trigger
        ref={triggerRef}
        type="button"
        style={colors}
        aria-label={t('common.account-menu')}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
      >
        {avatar}
      </Trigger>
    </Dropdown>
  )
}
